import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AccessLogDashboard.css';

const API_BASE_URL = 'https://rangers-api-3mfw.onrender.com';

const AccessLogDashboard = () => {
  const [logs, setLogs] = useState([]);
  const [userSummary, setUserSummary] = useState([]);
  const [dailyData, setDailyData] = useState([]);
  const [hourlyData, setHourlyData] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [browserData, setBrowserData] = useState([]);
  const [osData, setOsData] = useState([]);
  const [deviceData, setDeviceData] = useState([]);
  const [ipData, setIpData] = useState([]);
  const [pageData, setPageData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [authToken, setAuthToken] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [filterUser, setFilterUser] = useState('');
  const [filterPage, setFilterPage] = useState('');
  const [logLimit, setLogLimit] = useState(500);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      setAuthToken(token);
    } else {
      setIsLoading(false);
      setError('認証が必要です。ログインしてください。');
    }
  }, []);

  const fetchAllData = async () => {
    if (!authToken) return;
    try {
      setIsLoading(true);
      setError('');

      const headers = { headers: { Authorization: `Bearer ${authToken}` } };

      const [logsRes, userRes, dailyRes, browserRes, osRes, deviceRes, ipRes, pageRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/access-logs/logs?limit=${logLimit}`, headers),
        axios.get(`${API_BASE_URL}/api/access-logs/user-summary`, headers),
        axios.get(`${API_BASE_URL}/api/access-logs/daily-distribution`, headers),
        axios.get(`${API_BASE_URL}/api/access-logs/browser-distribution`, headers),
        axios.get(`${API_BASE_URL}/api/access-logs/os-distribution`, headers),
        axios.get(`${API_BASE_URL}/api/access-logs/device-distribution`, headers),
        axios.get(`${API_BASE_URL}/api/access-logs/ip-distribution`, headers),
        axios.get(`${API_BASE_URL}/api/access-logs/page-distribution`, headers)
      ]);

      setLogs(logsRes.data.logs || []);
      setUserSummary(userRes.data.users || []);
      setDailyData(dailyRes.data.data || []);

      // 最新の日付を選択状態に設定
      if (dailyRes.data.data && dailyRes.data.data.length > 0) {
        const latestDate = dailyRes.data.data[dailyRes.data.data.length - 1].date;
        setSelectedDate(latestDate);
      }

      setBrowserData(browserRes.data.data || []);
      setOsData(osRes.data.data || []);
      setDeviceData(deviceRes.data.data || []);
      setIpData(ipRes.data.data || []);
      setPageData(pageRes.data.data || []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError('データの取得に失敗しました。');
      if (err.response && err.response.status === 401) {
        setError('認証が無効です。ログインし直してください。');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // フィルタリングされたログ
  const filteredLogs = logs.filter(log => {
    if (filterUser && log.userId !== filterUser) return false;
    if (filterPage && log.pageType !== filterPage) return false;
    return true;
  });

  useEffect(() => {
    if (authToken) {
      fetchAllData();
    }
  }, [authToken]);

  // 選択日が変更されたら時間帯データを取得
  useEffect(() => {
    if (authToken && selectedDate) {
      const fetchHourlyData = async () => {
        try {
          const headers = { headers: { Authorization: `Bearer ${authToken}` } };
          const res = await axios.get(`${API_BASE_URL}/api/access-logs/hourly-by-date?date=${selectedDate}`, headers);
          setHourlyData(res.data.data || []);
        } catch (err) {
          console.error('Failed to fetch hourly data:', err);
        }
      };
      fetchHourlyData();
    }
  }, [selectedDate, authToken]);

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' });
  };

  const getEventTypeIcon = (eventType) => {
    switch (eventType) {
      case 'login': return '🔐';
      case 'api_access': return '📊';
      case 'auth_failed': return '❌';
      default: return '📋';
    }
  };

  const getPageTypeLabel = (pageType) => {
    const labels = {
      'CMA': '🇺🇸 CMA',
      'Nihon': '🇯🇵 Nihon',
      'CaN': '🇨 CaN',
      'GuildWarCount': '⚔️ GW Count',
      'PVP': '🎯 PVP',
      'AccessLogs': '📊 アクセスログ',
      'GuildInfo': '🏰 GuildInfo',
      'Instructions': '📖 攻略',
      'Ranking': '🏆 Ranking',
      'RangersBook': '📚 RangersBook',
      'DreamTeam': '⭐ DreamTeam',
      'AvailableTowers': '🗼 Towers',
      'Guardian': '🛡️ Guardian',
      'GuildWarStatus': '📊 GW Status',
      'other': 'その他'
    };
    return labels[pageType] || pageType;
  };

  const uniqueUsers = [...new Set(logs.map(l => l.userId))];
  const uniquePages = [...new Set(logs.map(l => l.pageType).filter(Boolean))];

  // 棒グラフコンポーネント
  const BarChart = ({ data, maxVal, labelKey, color = '#007bff' }) => {
    if (!data || data.length === 0) return <p>データなし</p>;
    const max = maxVal || Math.max(...data.map(d => d.count || d));
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {data.map((item, idx) => {
          const count = item.count || item;
          const label = item[labelKey || 'label'] || item.browser || item.os || item.device || item.ip || `${idx}時`;
          const pct = max > 0 ? (count / max * 100) : 0;
          return (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '140px', fontSize: '12px', textAlign: 'right', flexShrink: 0 }}>
                {label}
              </div>
              <div style={{ flex: 1, background: '#eee', borderRadius: '4px', height: '20px', overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, background: color, height: '100%', borderRadius: '4px', transition: 'width 0.3s' }} />
              </div>
              <div style={{ width: '40px', fontSize: '12px', textAlign: 'right' }}>{count}</div>
            </div>
          );
        })}
      </div>
    );
  };

  // 時間帯グラフコンポーネント
  const HourlyChart = ({ data }) => {
    if (!data || data.length === 0) return <p>データなし</p>;
    const max = Math.max(...data, 1);

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '120px', padding: '10px 0', borderBottom: '1px solid #eee' }}>
          {data.map((count, idx) => {
            const pct = max > 0 ? (count / max * 100) : 0;
            const isPeak = count === max && max > 0;
            return (
              <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ fontSize: '10px', marginBottom: '2px', fontWeight: isPeak ? 'bold' : 'normal', color: isPeak ? '#ff6b6b' : '#333' }}>
                  {count > 0 ? count : ''}
                </div>
                <div style={{
                  width: '100%',
                  height: `${Math.max(pct, 1)}%`,
                  background: isPeak ? '#ff6b6b' : (count > 0 ? '#007bff' : '#e9ecef'),
                  borderRadius: '2px 2px 0 0',
                  minHeight: '1px',
                  transition: 'height 0.3s'
                }} />
                <div style={{ fontSize: '9px', marginTop: '2px', color: isPeak ? '#ff6b6b' : '#666' }}>{idx}</div>
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#666', marginTop: '4px' }}>
          <span>00:00</span>
          <span>06:00</span>
          <span>12:00</span>
          <span>18:00</span>
          <span>23:00</span>
        </div>
      </div>
    );
  };

  // パイチャート風コンポーネント
  const PieChart = ({ data }) => {
    if (!data || data.length === 0) return <p>データなし</p>;
    const total = data.reduce((sum, d) => sum + d.count, 0);
    const colors = ['#007bff', '#28a745', '#ffc107', '#dc3545', '#17a2b8', '#6f42c1', '#fd7e14', '#e83e8c'];
    return (
      <div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
          {data.map((item, idx) => {
            const pct = total > 0 ? (item.count / total * 100).toFixed(1) : 0;
            return (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
                <div style={{ width: '12px', height: '12px', background: colors[idx % colors.length], borderRadius: '2px' }} />
                <span>{item.browser || item.os || item.device || item.ip}: {item.count}回 ({pct}%)</span>
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', height: '20px', borderRadius: '4px', overflow: 'hidden' }}>
          {data.map((item, idx) => {
            const pct = total > 0 ? (item.count / total * 100) : 0;
            return (
              <div key={idx} style={{ width: `${pct}%`, background: colors[idx % colors.length] }} />
            );
          })}
        </div>
      </div>
    );
  };

  if (!authToken) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h1>アクセスログダッシュボード</h1>
        <p style={{ color: 'red' }}>{error}</p>
        <p>ログインしてからご利用ください。</p>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: '📊 概要' },
    { id: 'logs', label: '📋 アクセスログ' },
    { id: 'pages', label: '📄 ページ別' },
    { id: 'users', label: '👤 ユーザー別' },
    { id: 'time', label: '📅 日別' },
    { id: 'browser', label: '🌐 ブラウザ/OS' },
    { id: 'ip', label: '🌍 IP別' }
  ];

  return (
    <div style={{ padding: isMobile ? '10px' : '20px', maxWidth: '1400px', margin: '0 auto', fontSize: isMobile ? '13px' : '14px' }}>
      <h1 style={{ fontSize: isMobile ? '20px' : '24px' }}>📊 アクセスログダッシュボード</h1>

      <button
        onClick={fetchAllData}
        style={{ padding: isMobile ? '6px 12px' : '8px 16px', background: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginBottom: '10px', fontSize: isMobile ? '12px' : '14px' }}
      >
        ⟳ 更新
      </button>

      {error && <p style={{ color: 'red', background: '#fee', padding: '10px' }}>{error}</p>}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '20px', borderBottom: '2px solid #ddd', paddingBottom: '8px' }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: isMobile ? '6px 10px' : '8px 16px',
              background: activeTab === tab.id ? '#007bff' : '#f0f0f0',
              color: activeTab === tab.id ? 'white' : '#333',
              border: 'none',
              borderRadius: '4px 4px 0 0',
              cursor: 'pointer',
              fontWeight: activeTab === tab.id ? 'bold' : 'normal',
              fontSize: isMobile ? '11px' : '13px'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p>読み込み中...</p>
      ) : (
        <>
          {/* 概要タブ */}
          {activeTab === 'overview' && (
            <div>
              <h2>📈 統計概要</h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: '8px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{logs.length}</div>
                  <div style={{ fontSize: '14px', color: '#666' }}>総アクセス数（最新{logs.length}件）</div>
                </div>
                <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: '8px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{userSummary.length}</div>
                  <div style={{ fontSize: '14px', color: '#666' }}>ユニークユーザー数</div>
                </div>
                <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: '8px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{ipData.length}</div>
                  <div style={{ fontSize: '14px', color: '#666' }}>ユニークIP数</div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px' }}>
                <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: '8px', padding: '16px' }}>
                  <h3>📅 日別アクセス</h3>
                  <BarChart data={dailyData} labelKey="date" color="#007bff" />
                </div>
                <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: '8px', padding: '16px' }}>
                  <h3>⏰ 時間帯別アクセス</h3>
                  <div style={{ marginBottom: '10px' }}>
                    <label>日付選択: </label>
                    <select
                      value={selectedDate}
                      onChange={e => setSelectedDate(e.target.value)}
                      className="filter-select"
                    >
                      {dailyData.map(d => (
                        <option key={d.date} value={d.date}>{d.date} ({d.count}回)</option>
                      ))}
                    </select>
                  </div>
                  {selectedDate && hourlyData.length > 0 ? (
                    <HourlyChart data={hourlyData} />
                  ) : (
                    <p>日付を選択してください</p>
                  )}
                </div>
                <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: '8px', padding: '16px' }}>
                  <h3>🌐 ブラウザ別</h3>
                  <PieChart data={browserData} />
                </div>
                <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: '8px', padding: '16px' }}>
                  <h3>💻 OS別</h3>
                  <PieChart data={osData} />
                </div>
                <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: '8px', padding: '16px' }}>
                  <h3>📱 デバイス別</h3>
                  <PieChart data={deviceData} />
                </div>
              </div>
            </div>
          )}

          {/* アクセスログ一覧タブ */}
          {activeTab === 'logs' && (
            <div>
              <h2>📋 アクセスログ一覧（{filteredLogs.length} 件 / 全 {logs.length} 件）</h2>

              {/* フィルタ */}
              <div style={{ marginBottom: '16px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <select value={filterUser} onChange={e => setFilterUser(e.target.value)} className="filter-select">
                  <option value="">全ユーザー</option>
                  {uniqueUsers.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
                <select value={filterPage} onChange={e => setFilterPage(e.target.value)} className="filter-select">
                  <option value="">全ページ</option>
                  {uniquePages.map(p => <option key={p} value={p}>{getPageTypeLabel(p)}</option>)}
                </select>
                <select value={logLimit} onChange={e => { setLogLimit(Number(e.target.value)); setTimeout(fetchAllData, 0); }} className="filter-select">
                  <option value={100}>100件</option>
                  <option value={500}>500件</option>
                  <option value={1000}>1000件</option>
                </select>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                  <thead>
                    <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                      <th style={{ padding: '6px', textAlign: 'left' }}>日時</th>
                      <th style={{ padding: '6px', textAlign: 'left' }}>タイプ</th>
                      <th style={{ padding: '6px', textAlign: 'left' }}>ページ</th>
                      <th style={{ padding: '6px', textAlign: 'left' }}>ユーザーID</th>
                      <th style={{ padding: '6px', textAlign: 'left' }}>IP</th>
                      <th style={{ padding: '6px', textAlign: 'left' }}>ブラウザ</th>
                      <th style={{ padding: '6px', textAlign: 'left' }}>OS</th>
                      <th style={{ padding: '6px', textAlign: 'left' }}>デバイス</th>
                      <th style={{ padding: '6px', textAlign: 'left' }}>URL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLogs.map((log, index) => (
                      <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '6px', whiteSpace: 'nowrap' }}>{formatTime(log.timestamp)}</td>
                        <td style={{ padding: '6px' }}>{getEventTypeIcon(log.eventType)}</td>
                        <td style={{ padding: '6px' }}>{getPageTypeLabel(log.pageType)}</td>
                        <td style={{ padding: '6px', fontFamily: 'monospace' }}>{log.userId}</td>
                        <td style={{ padding: '6px', fontFamily: 'monospace' }}>{log.ip}</td>
                        <td style={{ padding: '6px' }}>{log.browser || '-'}</td>
                        <td style={{ padding: '6px' }}>{log.os || '-'}</td>
                        <td style={{ padding: '6px' }}>{log.isMobile ? '📱' : '💻'} {log.device || '-'}</td>
                        <td style={{ padding: '6px', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.url}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ページ別タブ */}
          {activeTab === 'pages' && (
            <div>
              <h2>📄 ページ別アクセス</h2>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px' }}>
                <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: '8px', padding: '16px' }}>
                  <h3>ページ別アクセス数</h3>
                  <BarChart data={pageData} labelKey="page" color="#28a745" />
                </div>
              </div>
            </div>
          )}

          {/* ユーザー別タブ */}
          {activeTab === 'users' && (
            <div>
              <h2>👤 ユーザー別アクセス詳細</h2>
              <div style={{ display: 'grid', gap: '16px' }}>
                {userSummary.map((user, index) => (
                  <div key={index} style={{ background: '#fff', border: '1px solid #ddd', borderRadius: '8px', padding: '16px' }}>
                    <h3 style={{ margin: '0 0 10px 0' }}>
                      {user.userId} {user.isMobile && '📱'} {user.isBot && '🤖'}
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', fontSize: '13px' }}>
                      <div><strong>アクセス回数:</strong> {user.accessCount}</div>
                      <div><strong>初回アクセス:</strong> {formatTime(user.firstAccess)}</div>
                      <div><strong>最終アクセス:</strong> {formatTime(user.lastAccess)}</div>
                      <div><strong>IPアドレス:</strong> {user.ips.join(', ')}</div>
                      <div><strong>ブラウザ:</strong> {user.browsers.join(', ')}</div>
                      <div><strong>OS:</strong> {user.osList.join(', ')}</div>
                      <div><strong>デバイス:</strong> {user.devices.join(', ')}</div>
                      <div><strong>Origin:</strong> {user.origins.join(', ')}</div>
                    </div>
                    {/* IP詳細情報 */}
                    {user.ipDetails && user.ipDetails.length > 0 && (
                      <div style={{ marginTop: '12px', padding: '10px', background: '#f8f9fa', borderRadius: '6px' }}>
                        <h4 style={{ margin: '0 0 8px 0', fontSize: '14px' }}>🌍 IP住所情報</h4>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '8px', fontSize: '12px' }}>
                          {user.ipDetails.map((ipInfo, idx) => (
                            <div key={idx} style={{ padding: '6px', background: '#fff', borderRadius: '4px', border: '1px solid #eee' }}>
                              <div style={{ fontWeight: 'bold', fontFamily: 'monospace' }}>{ipInfo.ip}</div>
                              <div>🌍 {ipInfo.country || '-'} {ipInfo.countryCode === 'JP' ? '🇯🇵' : ipInfo.countryCode === 'US' ? '🇺🇸' : ipInfo.countryCode || ''}</div>
                              <div>📍 {ipInfo.region || '-'}, {ipInfo.city || '-'}</div>
                              <div>🏢 {ipInfo.org || '-'}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div style={{ marginTop: '10px' }}>
                      <strong>日別アクセス:</strong>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                        {Object.entries(user.dailyAccess || {})
                          .sort((a, b) => b[0].localeCompare(a[0])) // 日付の降順でソート
                          .slice(0, 14) // 直近14日間のみ表示
                          .map(([date, count]) => (
                            <div key={date} style={{ fontSize: '11px', background: '#f0f0f0', padding: '2px 6px', borderRadius: '4px' }}>
                              {date}: {count}回
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 日別タブ */}
          {activeTab === 'time' && (
            <div>
              <h2>📅 日別アクセス分布</h2>
              <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: '8px', padding: '16px' }}>
                <h3>全ユーザーの日別アクセス</h3>
                <BarChart data={dailyData} labelKey="date" color="#28a745" />
              </div>
            </div>
          )}

          {/* ブラウザ/OSタブ */}
          {activeTab === 'browser' && (
            <div>
              <h2>🌐 ブラウザ・OS・デバイス分布</h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px' }}>
                <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: '8px', padding: '16px' }}>
                  <h3>ブラウザ別</h3>
                  <BarChart data={browserData} labelKey="browser" />
                </div>
                <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: '8px', padding: '16px' }}>
                  <h3>OS別</h3>
                  <BarChart data={osData} labelKey="os" />
                </div>
                <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: '8px', padding: '16px' }}>
                  <h3>デバイス別</h3>
                  <BarChart data={deviceData} labelKey="device" />
                </div>
              </div>
            </div>
          )}

          {/* IP別タブ */}
          {activeTab === 'ip' && (
            <div>
              <h2>🌍 IP別アクセス（ジオロケーション情報付き）</h2>
              <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: '8px', padding: '16px' }}>
                <h3>IP別アクセス分布</h3>
                <BarChart data={ipData} labelKey="ip" color="#6f42c1" />
              </div>
              <div style={{ marginTop: '16px' }}>
                <h3>IP詳細情報</h3>
                {ipData.map((item, index) => (
                  <div key={index} style={{ background: '#fff', border: '1px solid #ddd', borderRadius: '8px', padding: '16px', marginBottom: '12px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '200px 1fr', gap: '8px', fontSize: '13px' }}>
                      <div style={{ fontWeight: 'bold' }}>📍 IPアドレス</div>
                      <div style={{ fontFamily: 'monospace', fontSize: '15px' }}>
                        {item.ip}
                        <span style={{ marginLeft: '10px', background: '#007bff', color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '12px' }}>
                          {item.count}回
                        </span>
                      </div>

                      <div style={{ fontWeight: 'bold' }}>🌐 ホスト名</div>
                      <div>{item.hostname || '-'}</div>

                      <div style={{ fontWeight: 'bold' }}>🌍 国</div>
                      <div>
                        {item.country || '-'}
                        {item.countryCode && (
                          <span style={{ marginLeft: '8px', fontSize: '16px' }}>
                            {item.countryCode === 'JP' ? '🇯🇵' : item.countryCode === 'US' ? '🇺🇸' : item.countryCode === 'TW' ? '🇹🇼' : item.countryCode === 'KR' ? '🇰' : item.countryCode}
                          </span>
                        )}
                      </div>

                      <div style={{ fontWeight: 'bold' }}>📍 地域</div>
                      <div>{item.region || '-'}</div>

                      <div style={{ fontWeight: 'bold' }}>🏙️ 都市</div>
                      <div>{item.city || '-'}</div>

                      <div style={{ fontWeight: 'bold' }}>📮 郵便番号</div>
                      <div>{item.postal || '-'}</div>

                      <div style={{ fontWeight: 'bold' }}>🗺️ 緯度・経度</div>
                      <div>
                        {item.latitude !== '-' && item.longitude !== '-' ? (
                          <>
                            {item.latitude}, {item.longitude}
                            <a
                              href={`https://www.google.com/maps?q=${item.latitude},${item.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ marginLeft: '8px', color: '#007bff', fontSize: '12px' }}
                            >
                              地図で見る →
                            </a>
                          </>
                        ) : '-'}
                      </div>

                      <div style={{ fontWeight: 'bold' }}>🏢 組織</div>
                      <div>{item.org || '-'}</div>

                      <div style={{ fontWeight: 'bold' }}>👤 関連ユーザーID</div>
                      <div style={{ fontFamily: 'monospace' }}>{item.userIds.join(', ')}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AccessLogDashboard;
